/** How a hint's content is rendered. Nodes without a finished clip stay on `'text'`. */
export type HintContentMode = 'text' | 'text+video' | 'video';

/**
 * Clip framing in the modal/card. Default is square (narrow card, ~half width on manage pages).
 * Wide stretches the host — Skills, Connectors overview, Quality overview, and Exploratory.
 */
export type HintVideoAspect = 'square' | 'wide';

/**
 * A single onboarding hint. The config is a flat record keyed by id, not a tree — parent/child
 * visibility is expressed by where React mounts the matching `useHintTarget`, not by this shape.
 */
export interface HintNode {
  id: string;
  title: string;
  body: string;
  mode: HintContentMode;
  videoSrc?: string;
  videoAspect?: HintVideoAspect;
  /**
   * Horizontal beacon column. `sidebar` pins X to the nav content rail (nearest `.px-3` /
   * `.sidebar-root`) so rows with a trailing chevron or gear still line up with full-width links.
   * Default is the host element's own right edge.
   */
  beaconAlign?: 'host' | 'sidebar';
  /**
   * Nudges the beacon away from something already sitting in the host's top-end corner, such as a
   * chevron or a badge. Pixels relative to the default inset corner; negative x moves it inward.
   */
  beaconOffset?: { x?: number; y?: number };
}

/**
 * The full set of onboarding hints, keyed by id. Every node's copy is anchored to the component it
 * describes so the tour never promises a capability the app does not have.
 */
export const HINTS: Readonly<Record<string, HintNode>> = {
  'nav.workspaces': {
    id: 'nav.workspaces',
    title: 'Switch workspaces',
    body: 'This switcher jumps between every workspace you belong to, with your personal workspace pinned to the top. Pick one to scope chats, connectors, and settings to that team or project.',
    mode: 'text+video',
    videoSrc: '/onboarding/workspaces.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.workspaces.create': {
    id: 'nav.workspaces.create',
    title: 'Create a workspace',
    body: 'A workspace groups chats, connectors, and settings for a team or a project. Create one to start sharing that context with teammates, instead of keeping everything in your personal workspace.',
    mode: 'text',
    beaconAlign: 'sidebar',
  },
  'nav.automations': {
    id: 'nav.automations',
    title: 'Scheduled',
    body: 'Schedule a prompt to run on its own, like a daily check-in or a weekly reminder. Whatever you schedule with a dedicated chat shows up here too, so you can open it, edit its rule, or fire it early.',
    mode: 'text+video',
    videoSrc: '/onboarding/automations.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.artifacts': {
    id: 'nav.artifacts',
    title: 'Artifacts',
    body: 'Files the agent builds in chat land here. Preview them, find Agent-created ones in the library, and share with the workspace when you are ready.',
    mode: 'text+video',
    videoSrc: '/onboarding/artifacts.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.decks': {
    id: 'nav.decks',
    title: 'Decks',
    body: 'Every deck the agent builds for you lands here, tagged Ready, Generating, Failed, or Archived. Search past decks or download a finished one straight from the list.',
    mode: 'text+video',
    videoSrc: '/onboarding/decks.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.memory': {
    id: 'nav.memory',
    title: 'Memory',
    body: 'What this workspace knows lives here: decisions, facts and conventions the whole team shares, plus a private set only you can read. Suggestions found in your chats wait on the Suggestions tab until someone approves them, and every entry keeps its own history.',
    mode: 'text+video',
    videoSrc: '/onboarding/memory.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.dashboards': {
    id: 'nav.dashboards',
    title: 'Dashboards',
    body: 'Ask the agent for a chart or a data view in chat, then save it here as a live dashboard. Saved dashboards refresh on their own, and you can reorder or remove them anytime.',
    mode: 'text+video',
    videoSrc: '/onboarding/dashboards.mp4',
    videoAspect: 'square',
    beaconAlign: 'sidebar',
  },
  'nav.skills': {
    id: 'nav.skills',
    title: 'Skills',
    body: 'A skill is a set of instructions you save once and run whenever you need it, instead of retyping them. Browse what your company already publishes, fork any of them into a copy you can edit, or write your own.',
    mode: 'text+video',
    videoSrc: '/onboarding/skills.mp4',
    videoAspect: 'wide',
    beaconAlign: 'sidebar',
  },
  'composer.plus': {
    id: 'composer.plus',
    title: 'More actions',
    body: "This button opens everything the composer can add to your message: files, connectors, and skills. It's the fastest way to bring outside data or a saved instruction into the conversation.",
    mode: 'text',
  },
  'composer.plus.connectors': {
    id: 'composer.plus.connectors',
    title: 'Connectors',
    body: 'Browse and connect Customertimes and partner tools like Jira, Outlook, and GitHub. Once connected, the agent can pull real data from them straight into chat.',
    mode: 'text+video',
    videoSrc: '/onboarding/connectors.mp4',
    videoAspect: 'wide',
  },
  'composer.plus.skills': {
    id: 'composer.plus.skills',
    title: 'Skills',
    body: 'Skills are reusable instructions you can run on demand instead of retyping them. Pick one here and it drops straight into your message as a starting point.',
    mode: 'text+video',
    videoSrc: '/onboarding/skills.mp4',
    videoAspect: 'wide',
  },
  'connector.jira': {
    id: 'connector.jira',
    title: 'Jira',
    body: 'Connect Jira to let the agent find issues, track sprint progress, and pull ticket context into chat. Try asking: "What\'s blocking PROJ-482?"',
    mode: 'text+video',
    videoSrc: '/onboarding/connector-jira.mp4',
    videoAspect: 'square',
  },
  'connector.outlook': {
    id: 'connector.outlook',
    title: 'Outlook',
    body: 'Connect Outlook to let the agent search your mail, check your calendar, and pull up contact details. Try asking: "What\'s on my calendar tomorrow afternoon?"',
    mode: 'text+video',
    videoSrc: '/onboarding/connector-outlook.mp4',
    videoAspect: 'square',
  },
  'connector.github': {
    id: 'connector.github',
    title: 'GitHub',
    body: 'Connect GitHub to let the agent look at your repositories, issues, and pull requests on your behalf. Try asking: "List open pull requests waiting on my review in cti-web-interface."',
    mode: 'text',
  },
  'workspace.tab.knowledge': {
    id: 'workspace.tab.knowledge',
    title: 'Knowledge',
    body: 'Add SharePoint folders or files as sources the agent can search when answering in this workspace. Each source shows its sync status, and you can enable or remove it anytime.',
    mode: 'text',
  },
  'workspace.tab.credentials': {
    id: 'workspace.tab.credentials',
    title: 'Credentials',
    body: "This tab lists the credential records saved for this workspace's services. Anyone can see the metadata; adding or removing a credential needs manage-settings access.",
    mode: 'text',
  },
  'workspace.tab.quality': {
    id: 'workspace.tab.quality',
    title: 'Quality',
    body: "Quality is CT Agent test automation — explore a live app, generate cases, and catch regressions. Turn the domain on here to provision this workspace's test project; until then the tab shows just the toggle.",
    mode: 'text+video',
    videoSrc: '/onboarding/quality.mp4',
    videoAspect: 'wide',
  },
  'quality.exploratory': {
    id: 'quality.exploratory',
    title: 'Exploratory test',
    body: 'Give the agent a URL, a focus, and a time box — it browses your live app on its own, then hands back every step it took and every bug it found. No test cases are written.',
    mode: 'text+video',
    videoSrc: '/onboarding/quality-exploratory.mp4',
    videoAspect: 'wide',
  },
  'quality.regression': {
    id: 'quality.regression',
    title: 'Regression',
    body: 'Schedule regression jobs with a cadence and Teams alerts on fail. Each job keeps a run history, and you can kick one with Run now when you need it.',
    mode: 'text+video',
    videoSrc: '/onboarding/quality-regression.mp4',
    videoAspect: 'square',
  },
  'quality.generate': {
    id: 'quality.generate',
    title: 'Generate tests',
    body: 'Pick Docs, Explore, Jira, or existing cases as sources. Review what the agent synthesized, then write the approved set to your test management system.',
    mode: 'text+video',
    videoSrc: '/onboarding/quality-generate.mp4',
    videoAspect: 'square',
  },
  'workspace.tab.developer': {
    id: 'workspace.tab.developer',
    title: 'Developer',
    body: "Turn on the Developer domain to provision this workspace's Digital Developer settings and configure them here. It defaults off, so nothing runs until you switch it on.",
    mode: 'text',
  },
};

/**
 * The intro card shown once at the start of the tour, before any individual hint. `suggestedPrompt`
 * is the question prefilled into the composer if the user takes the primary action.
 */
export interface OnboardingIntro {
  title: string;
  body: string;
  primaryButtonLabel: string;
  suggestedPrompt: string;
  mode: HintContentMode;
  videoSrc?: string;
  videoAspect?: HintVideoAspect;
}

export const INTRO: OnboardingIntro = {
  title: 'Welcome to CT Agent',
  body: 'One chat connected to your tools. We will place a starter question in the box — press Send to ask what the agent can do. After that, soft lights mark the controls worth exploring.',
  primaryButtonLabel: 'Get started',
  suggestedPrompt: 'What can you do?',
  mode: 'text+video',
  videoSrc: '/onboarding/intro.mp4',
  videoAspect: 'square',
};
